/**
 * Utilidades de data — evita o clássico bug de "1 dia a menos".
 *
 * # Contexto
 * Datas de curso (start_date / end_date) são conceitualmente "dias-calendário"
 * (não instantes no tempo). Ex.: "curso começa em 28/07/2026" — sem hora.
 *
 * O bug clássico: `new Date("2026-07-28")` interpreta como **UTC midnight**
 * (per ISO 8601). No fuso do Brasil (UTC-3), isso vira 27/07 21:00, e
 * `.toLocaleDateString("pt-BR")` retorna "27/07/2026" — 1 dia antes.
 *
 * # Solução
 * Extraímos ano/mês/dia da string e construímos o Date no fuso LOCAL
 * (usa o construtor `new Date(y, m-1, d)` que **NÃO** aplica offset).
 * Isso torna a data estável em qualquer fuso, mantendo o dia-calendário.
 */

/**
 * Extrai a parte `YYYY-MM-DD` de um valor de data vindo do backend.
 * Aceita:
 *  - "2026-07-28"                    → "2026-07-28"
 *  - "2026-07-28T00:00:00"           → "2026-07-28"
 *  - "2026-07-28T00:00:00.000Z"      → "2026-07-28"
 *  - "2026-07-28T03:00:00-03:00"     → "2026-07-28"
 *  - Date object                     → normaliza para local YYYY-MM-DD
 *  - undefined/null/""               → null
 */
export function parseYMD(value) {
  if (!value) return null;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return null;
}

/**
 * Constrói um Date **no fuso LOCAL** a partir de uma string ou Date.
 * Uso para renderização de dias-calendário. NÃO usar para instantes.
 */
export function toLocalDate(value) {
  const ymd = parseYMD(value);
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  // O construtor com args numéricos usa fuso local — sem offset UTC.
  return new Date(y, m - 1, d);
}

/**
 * Formata uma data-calendário como "DD/MM/YYYY" no padrão pt-BR, SEM
 * qualquer conversão de fuso.
 */
export function formatCourseDate(value) {
  const ymd = parseYMD(value);
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Formata "26/07 – 30/07/2026" ou "26/07 – 30/08/2026" ou "26/07/2026 – 03/08/2026"
 * dependendo se o mês/ano coincidem (compacto).
 */
export function formatCourseDateRange(startValue, endValue) {
  const s = parseYMD(startValue);
  const e = parseYMD(endValue);
  if (!s && !e) return "";
  if (!s) return formatCourseDate(e);
  if (!e) return formatCourseDate(s);
  const [sy, sm, sd] = s.split("-");
  const [ey, em, ed] = e.split("-");
  if (sy === ey && sm === em) return `${sd}/${sm} – ${ed}/${em}/${ey}`;
  if (sy === ey) return `${sd}/${sm} – ${ed}/${em}/${ey}`;
  return `${sd}/${sm}/${sy} – ${ed}/${em}/${ey}`;
}

/**
 * Serializa uma data-calendário para enviar ao backend.
 * NÃO aplica timezone. Preserva o dia-calendário exatamente.
 * Retorna "YYYY-MM-DD" (ISO date only).
 */
export function toBackendDate(value) {
  return parseYMD(value);
}

/**
 * Compara duas datas-calendário (dia-a-dia, sem fuso).
 * Retorna -1, 0, 1.
 */
export function compareYMD(a, b) {
  const A = parseYMD(a);
  const B = parseYMD(b);
  if (A === B) return 0;
  if (!A) return -1;
  if (!B) return 1;
  return A < B ? -1 : 1;
}
